import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useEntityAction } from "@/lib/mutations";
import { Button, ErrorNote } from "@/components/ui";
import { Select } from "@/components/Form";
import type { CostLineOption } from "@/components/FinanceLineForm";

/**
 * «Изменить» on an extra cost (owner rule 19): move it to another line of its booking
 * without voiding it. "" = automatic — the first line with a car / driver; a ticket
 * only when it is picked here by hand. The window closes and «Сохранено» shows once
 * the server has taken it (`useEntityAction`).
 */
export function CostLineEditor({
  costId,
  chosenLineId,
  lines,
  invalidate,
  onDone,
  onCancel,
}: {
  costId: string;
  /** The line the owner chose for this cost (`booking_line_id`); null = automatic. */
  chosenLineId: string | null;
  lines: CostLineOption[];
  invalidate: unknown[][];
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(chosenLineId ?? "");
  const save = useEntityAction(invalidate, { inlineError: true, onSuccess: onDone });

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Select
        aria-label={t("bookings.costLine")}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      >
        <option value="">{t("bookings.costLineAuto")}</option>
        {lines.map((l) => (
          <option key={l.id} value={l.id}>{l.label}</option>
        ))}
      </Select>
      <Button
        className="px-3 py-1 text-xs"
        loading={save.isPending}
        onClick={() =>
          save.mutate(() =>
            api.patch(`/finance/costs/${costId}`, { booking_line_id: value || null }),
          )
        }
      >
        {t("common.save")}
      </Button>
      <button className="text-xs text-sand-muted hover:text-sand" onClick={onCancel}>
        {t("common.cancel")}
      </button>
      {save.isError && <ErrorNote message={(save.error as Error).message} />}
    </div>
  );
}
